/**
 * Parses assistant message for embedded finance-classify block (from suggest_classification).
 * Used to drive the BankLineClassifyCard in the finance chat panel.
 *
 * The agent outputs: <finance-classify>{"bank_line_id": "...", "suggested_type": "...", ...}</finance-classify>
 */

import type { ClassificationType } from '../types/bankStatements';

export interface FinanceClassifyPayload {
  bank_line_id: string;
  suggested_type: ClassificationType;
  confidence: number;
  reasoning: string;
  confidence_level?: 'high' | 'medium-high' | 'medium' | 'low';
  matched_pattern?: string | null;
}

export interface ExtractFinanceClassifyResult {
  classifyPayload: FinanceClassifyPayload | null;
  contentWithoutBlock: string;
}

const OPEN_TAG = '<finance-classify>';
const CLOSE_TAG = '</finance-classify>';

function isValidPayload(obj: unknown): obj is FinanceClassifyPayload {
  if (obj == null || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  return (
    typeof o.bank_line_id === 'string' &&
    typeof o.suggested_type === 'string' &&
    typeof o.confidence === 'number' &&
    typeof o.reasoning === 'string'
  );
}

/**
 * Extracts a <finance-classify> tag block from assistant message content.
 * Returns the parsed payload (if valid) and the message with that block removed.
 */
export function extractFinanceClassifyBlock(content: string): ExtractFinanceClassifyResult {
  const openIdx = content.indexOf(OPEN_TAG);
  if (openIdx === -1) {
    return { classifyPayload: null, contentWithoutBlock: content };
  }

  const closeIdx = content.indexOf(CLOSE_TAG, openIdx);
  if (closeIdx === -1) {
    return { classifyPayload: null, contentWithoutBlock: content };
  }

  const jsonStr = content.slice(openIdx + OPEN_TAG.length, closeIdx).trim();
  const blockEnd = closeIdx + CLOSE_TAG.length;
  const contentWithoutBlock = content.slice(0, openIdx).trimEnd() + (blockEnd < content.length ? content.slice(blockEnd).trimStart() : '');

  try {
    const parsed = JSON.parse(jsonStr) as unknown;
    if (!isValidPayload(parsed)) {
      return { classifyPayload: null, contentWithoutBlock };
    }
    return { classifyPayload: parsed, contentWithoutBlock };
  } catch {
    return { classifyPayload: null, contentWithoutBlock };
  }
}
